import { Link } from "react-router-dom";
import { Wrench, ArrowLeft } from "lucide-react";

const CONTACT_EMAIL = "privacy@ironlog.au";
const EFFECTIVE = "July 2026";

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-head font-900 uppercase tracking-tight text-xl md:text-2xl text-white mb-3">{title}</h2>
      <div className="font-body text-[#C7CED8] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Terms() {
  return (
    <div className="grain relative bg-[#0D0E11] min-h-screen text-white" data-testid="terms-page">
      <header className="border-b border-white/10">
        <div className="max-w-[900px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="terms-home-link">
            <div className="w-8 h-8 bg-[#FFB800] flex items-center justify-center"><Wrench className="w-5 h-5 text-black" strokeWidth={2.5} /></div>
            <span className="font-head font-900 text-2xl tracking-tight">IRONLOG</span>
          </Link>
          <Link to="/" className="font-mono text-xs text-[#94A3B8] hover:text-[#FFB800] flex items-center gap-2" data-testid="terms-back-link">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 py-14">
        <p className="font-mono text-xs uppercase tracking-widest text-[#FFB800] mb-3">Legal</p>
        <h1 className="font-head font-900 uppercase tracking-tight text-4xl sm:text-5xl mb-3">Terms of Service</h1>
        <p className="font-mono text-sm text-[#94A3B8] mb-12">Effective {EFFECTIVE}</p>

        <Section title="1. Acceptance of These Terms">
          <p>
            These Terms of Service (&quot;Terms&quot;) govern your access to and use of IronLog (&quot;the service&quot;, &quot;we&quot;, &quot;us&quot;).
            By creating an account or using the service, you agree to these Terms. If you do not agree, do not use IronLog.
          </p>
        </Section>

        <Section title="2. The Service">
          <p>
            IronLog is a machinery maintenance and fleet management platform that lets you record machines, log services,
            set reminders, assign work orders and manage your team. Features available to you depend on your subscription plan.
          </p>
        </Section>

        <Section title="3. Accounts & Fleets">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>You are responsible for keeping your login credentials secure and for all activity under your account.</li>
            <li>Managers control their fleet, including inviting and removing team members and managing billing.</li>
            <li>You must provide accurate information and are responsible for the data you enter.</li>
          </ul>
        </Section>

        <Section title="4. Subscriptions & Payments">
          <p>
            Paid plans are billed in Australian Dollars (AUD) through our payment processor, Stripe. Plan limits (such as
            the number of machines and team members) apply as described at sign-up. It is your responsibility to manage
            your subscription. See our <Link to="/privacy" className="text-[#FFB800] hover:underline">Privacy Policy</Link> for
            how payment information is handled.
          </p>
        </Section>

        <Section title="5. Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Use the service for any unlawful purpose or in breach of any applicable law.</li>
            <li>Attempt to gain unauthorised access to the service, other accounts, or our systems.</li>
            <li>Interfere with or disrupt the integrity or performance of the service.</li>
            <li>Upload content you do not have the right to use, or that is unlawful or harmful.</li>
          </ul>
        </Section>

        <Section title="6. QR Codes & Public Machine Records">
          <p className="border-l-2 border-[#FFB800] pl-4 bg-white/[0.03] py-3">
            IronLog can generate QR codes for machines. When enabled, scanning a machine&apos;s QR code displays a limited,
            public summary of that machine (such as make, model, type, running hours and a service history summary) to
            anyone who scans it, including people without an IronLog account. Sensitive details such as serial numbers,
            registration numbers, costs, notes and personal names are not shown. If you print or attach a QR code to a
            machine, you accept that this public summary may be viewed by third parties.
          </p>
        </Section>

        <Section title="7. Your Data & Backups">
          <p>
            You retain ownership of the data you enter. You can export a full copy of your fleet data at any time from
            Settings → Data Backup. We strongly recommend keeping your own regular backups.
          </p>
        </Section>

        <Section title="8. Service Provided &quot;As Is&quot; — No Warranty">
          <p>
            The service is provided on an <span className="text-white font-600">&quot;as is&quot; and &quot;as available&quot;</span> basis.
            To the maximum extent permitted by law, we make no warranties or representations, express or implied, that the
            service will be uninterrupted, error-free, secure, or that reminders, notifications or data will always be
            delivered, accurate or retained. IronLog is a tool to assist with maintenance record-keeping and does not
            replace your own professional judgement, safety checks or compliance obligations.
          </p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p className="border-l-2 border-[#FFB800] pl-4 bg-white/[0.03] py-3">
            To the maximum extent permitted by law, IronLog and its owners are <span className="text-white font-600">not
            liable</span> for any indirect, incidental, special or consequential loss, or for any loss of profits, revenue,
            data, or business, arising from your use of (or inability to use) the service — including any loss, corruption
            or unavailability of data occurring on the underlying servers or hosting infrastructure, or any missed reminder,
            service or registration. Nothing in these Terms excludes rights or guarantees that cannot be excluded under the
            Australian Consumer Law; where our liability cannot be excluded, it is limited (at our option) to re-supplying
            the service or paying the cost of re-supply.
          </p>
        </Section>

        <Section title="10. Suspension & Termination">
          <p>
            We may suspend or terminate access to accounts that breach these Terms or that we reasonably believe pose a
            risk to the service or other users. You may stop using the service at any time.
          </p>
        </Section>

        <Section title="11. Changes to These Terms">
          <p>
            We may update these Terms from time to time. Material changes will be communicated through the app or by email.
            Continued use of the service after changes take effect constitutes acceptance of the updated Terms.
          </p>
        </Section>

        <Section title="12. Governing Law">
          <p>
            These Terms are governed by the laws of Australia. Any disputes are subject to the jurisdiction of the
            Australian courts.
          </p>
        </Section>

        <Section title="13. Contact Us">
          <p>
            Questions about these Terms? Contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FFB800] hover:underline" data-testid="terms-contact-email">{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <div className="border-t border-white/10 pt-6 mt-4 font-mono text-xs text-[#94A3B8]">
          © {new Date().getFullYear()} IRONLOG · Built rugged in AUD
        </div>
      </main>
    </div>
  );
}
